import { createSelector } from '@reduxjs/toolkit';

import { RootState } from './store';

// Base selectors
export const selectWebSocketState = (state: RootState) => state.websocket;

export const selectConnections = createSelector(
  selectWebSocketState,
  (websocket) => websocket.connections
);

export const selectWSMessages = createSelector(
  selectWebSocketState,
  (websocket) => websocket.messages
);
// Connection selectors
export const selectConnectionById = createSelector(
  [selectConnections, (_, connectionId: string) => connectionId],
  (connections, connectionId) => connections[connectionId]
);

export const selectConnectedConnections = createSelector(
  selectConnections,
  (connections) =>
    Object.entries(connections)
      .filter(([_, state]) => state.isConnected)
      .map(([id, state]) => ({ id, ...state }))
);

export const selectDisconnectedConnections = createSelector(
  selectConnections,
  (connections) =>
    Object.entries(connections)
      .filter(([_, state]) => !state.isConnected)
      .map(([id, state]) => ({ id, ...state }))
);

export const selectConnectionErrors = createSelector(
  selectConnections,
  (connections) =>
    Object.entries(connections)
      .filter(([_, state]) => state.error)
      .map(([id, state]) => ({ id, error: state.error }))
);

// Message selectors
export const selectMessagesByType = createSelector(
  [selectWSMessages, (_, messageType: string) => messageType],
  (messages, messageType) => messages.filter((msg) => msg.type === messageType)
);

export const selectLatestMessageByType = createSelector(
  [selectWSMessages, (_, messageType: string) => messageType],
  (messages, messageType) => {
    const filteredMessages = messages.filter((msg) => msg.type === messageType);
    return filteredMessages[filteredMessages.length - 1];
  }
);

// Statistics selectors
export const selectConnectionStats = createSelector(
  selectConnections,
  (connections) => {
    const total = Object.keys(connections).length;
    const connected = Object.values(connections).filter(
      (c) => c.isConnected
    ).length;
    const connecting = Object.values(connections).filter(
      (c) => c.isConnecting
    ).length;
    const reconnecting = Object.values(connections).filter(
      (c) => c.isReconnecting
    ).length;
    const errors = Object.values(connections).filter((c) => c.error).length;

    return {
      total,
      connected,
      connecting,
      reconnecting,
      errors,
      disconnected: total - connected - connecting - reconnecting,
    };
  }
);
